import { useEffect, useState } from "react"
import { Table } from "reactstrap"
import ActionButton from "Components/atoms/buttons/action-button"
import IconButton from "Components/atoms/buttons/icon-button"
import { PencilIcon, PlusIcon, TrashIcon } from "Components/atoms/icons"
import CostCodeFormModal from "./cost-code-form-modal"
import {
    CostCodePayload, ExpenseBudget, ExpenseBudgetCostCode, PlanVsActual, PlanView,
} from "models/expense-budget"
import {
    createExpenseBudgetCostCode, deleteExpenseBudgetCostCode, getExpenseBudgetPlanVsActual, updateExpenseBudgetCostCode,
} from "slices/finance/expense-budget/thunk"
import { sumAmounts } from "utils/expense-budget"
import { formatCurrency } from "utils/tools"

interface CostCodesTabProps {
    budget: ExpenseBudget
    onBudgetChanged: () => Promise<void>
}

// Each cost code (project + GL account) with approved vs planned vs actual vs forecast.
const CostCodesTab = ({ budget, onBudgetChanged }: CostCodesTabProps) => {
    const [planVsActual, setPlanVsActual] = useState<PlanVsActual | null>(null)
    const [editingCostCode, setEditingCostCode] = useState<ExpenseBudgetCostCode | null>(null)
    const [isFormOpen, setIsFormOpen] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        getExpenseBudgetPlanVsActual(budget.id, PlanView.Accounting).then(setPlanVsActual)
    }, [budget])

    const openForm = (costCode: ExpenseBudgetCostCode | null) => {
        setEditingCostCode(costCode)
        setIsFormOpen(true)
    }

    const handleSave = async (payload: CostCodePayload) => {
        setIsSaving(true)
        const saved = editingCostCode
            ? await updateExpenseBudgetCostCode(budget.id, editingCostCode.id, payload)
            : await createExpenseBudgetCostCode(budget.id, payload)
        setIsSaving(false)
        if (!saved) return
        setIsFormOpen(false)
        await onBudgetChanged()
    }

    const handleDelete = async (costCode: ExpenseBudgetCostCode) => {
        if (!window.confirm(`Remove cost code ${costCode.transactionAccount.code.trim()}?`)) return
        if (await deleteExpenseBudgetCostCode(budget.id, costCode.id)) await onBudgetChanged()
    }

    const totalsOf = (costCodeId: string) => {
        const row = planVsActual?.costCodes.find(c => c.id === costCodeId)
        if (!row) return null
        const actualToDate = sumAmounts(row.actual.filter((_, i) => planVsActual!.periods[i] < planVsActual!.firstOpenPeriod))
        return { planned: sumAmounts(row.planned), actualToDate, forecast: row.forecast }
    }

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex justify-content-end">
                <ActionButton onClick={() => openForm(null)}>Add Cost Code <PlusIcon /></ActionButton>
            </div>
            <Table size="sm" hover className="align-middle">
                <thead>
                    <tr>
                        <th>Project</th><th>GL Account</th>
                        <th className="text-end">Approved</th><th className="text-end">Planned</th>
                        <th className="text-end">Actual to date</th><th className="text-end">Forecast</th><th />
                    </tr>
                </thead>
                <tbody>
                    {budget.costCodes.map(costCode => {
                        const totals = totalsOf(costCode.id)
                        const isForecastOver = totals && Number(costCode.approvedAmount) > 0 && totals.forecast > Number(costCode.approvedAmount)
                        return (
                            <tr key={costCode.id}>
                                <td>{costCode.project ? costCode.project.code.trim() : "Any"}</td>
                                <td>{costCode.transactionAccount.code.trim()} — {costCode.transactionAccount.description}</td>
                                <td className="text-end">{formatCurrency(costCode.approvedAmount)}</td>
                                <td className="text-end">{totals ? formatCurrency(totals.planned) : "…"}</td>
                                <td className="text-end">{totals ? formatCurrency(totals.actualToDate) : "…"}</td>
                                <td className={`text-end ${isForecastOver ? "text-danger fw-bold" : ""}`}>{totals ? formatCurrency(totals.forecast) : "…"}</td>
                                <td>
                                    <div className="d-flex gap-1 justify-content-end">
                                        <IconButton onClick={() => openForm(costCode)}><PencilIcon /></IconButton>
                                        <IconButton onClick={() => handleDelete(costCode)}><TrashIcon /></IconButton>
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </Table>
            <CostCodeFormModal isOpen={isFormOpen} budget={budget} costCode={editingCostCode} isSaving={isSaving}
                onClose={() => setIsFormOpen(false)} onSave={handleSave} />
        </div>
    )
}

export default CostCodesTab
